/**
 * Sentry event scrubbing that keeps PHI out of error monitoring.
 *
 * Sentry has no BAA with us, so it receives the shape of an error and nothing
 * about the patient. The backend takes the same line in
 * src/utils/sentry_config.py; this is the browser half.
 */

/**
 * Breadcrumb categories whose message is a URL or an HTTP result, never
 * page content. Every other category is redacted.
 *
 * The categories the browser SDK emits are console, dom, ui.click, ui.input,
 * navigation, fetch, xhr, sentry.event and sentry.transaction. The ui.* ones
 * are the reason this is an allowlist and not a denylist: their message is the
 * clicked element serialized by htmlTreeAsString, which includes the
 * aria-label, title, alt, name and type attributes. CaseManagement puts the
 * patient's name in an aria-label, and the upload panels put the document
 * filename in a title, so a single click used to carry both to Sentry.
 */
const MESSAGE_SAFE_CATEGORIES = new Set(['navigation', 'fetch', 'xhr']);

/** Keys kept on an allowlisted breadcrumb's data. Anything else is dropped. */
const SAFE_DATA_KEYS = ['method', 'status_code', 'url', 'from', 'to'];

/**
 * The URL-shaped keys among those, whose query string is dropped.
 *
 * Keeping the path is what makes a trail readable; keeping the query is what
 * leaks. `CodeSearchModal` sends the clinician's typed search term to
 * `/icd/search?q=`, and a navigation breadcrumb's `from` can be
 * `/register?code=` with an invite code on it. `dataCollection.urlQueryParams`
 * closes the span half of this; it does not reach a breadcrumb, because the
 * breadcrumb url is recorded by the fetch and xhr instrumentation.
 */
const URL_DATA_KEYS = ['url', 'from', 'to'];

/**
 * A URL-shaped value with its query string and fragment removed.
 *
 * @param {*} value - A breadcrumb data value or span attribute.
 *
 * Used on breadcrumb data, span url attributes and `event.request.url`.
 *
 * @returns {*} The value up to the first `?` or `#`, or the value unchanged
 *   when it is not a string.
 */
function stripQuery(value) {
  return typeof value === 'string' ? value.split(/[?#]/)[0] : value;
}

/**
 * The event's breadcrumbs, whichever shape this SDK version uses.
 *
 * v10 and earlier handed `beforeSend` an event with `breadcrumbs.values`; v11
 * hands it a plain array. Reading `.values` off an array does not fail loudly:
 * it returns `Array.prototype.values`, a function, which is truthy. So the old
 * guard passed and the loop then threw "breadcrumbs is not iterable", and a
 * `beforeSend` that throws makes the SDK drop the event. Every error with a
 * breadcrumb on it, which is nearly all of them, would have gone missing on
 * upgrade, and the only symptom would have been a quiet Sentry.
 *
 * @param {object} event - A Sentry event.
 *
 * @returns {Array|null} The breadcrumb array, or null when there is none.
 */
function breadcrumbList(event) {
  const breadcrumbs = event.breadcrumbs;
  if (Array.isArray(breadcrumbs)) return breadcrumbs;
  if (Array.isArray(breadcrumbs?.values)) return breadcrumbs.values;
  return null;
}

function scrubBreadcrumbs(event) {
  const breadcrumbs = breadcrumbList(event);
  if (!breadcrumbs) return;

  for (const breadcrumb of breadcrumbs) {
    if (MESSAGE_SAFE_CATEGORIES.has(breadcrumb.category)) {
      if (breadcrumb.data) {
        for (const key of Object.keys(breadcrumb.data)) {
          if (!SAFE_DATA_KEYS.includes(key)) {
            delete breadcrumb.data[key];
          }
        }
        for (const key of URL_DATA_KEYS) {
          if (key in breadcrumb.data) {
            breadcrumb.data[key] = stripQuery(breadcrumb.data[key]);
          }
        }
      }
      continue;
    }

    breadcrumb.message = '[redacted]';
    delete breadcrumb.data;
  }
}

function scrubStackFrames(event) {
  const values = event.exception?.values;
  if (!values) return;

  for (const entry of values) {
    for (const frame of entry.stacktrace?.frames || []) {
      delete frame.vars;
    }
  }
}

/**
 * before-send hook. Removes every part of an event that can carry patient data
 * and returns the event, so Sentry still receives the error type, message and
 * file and line information.
 */
export function scrubEvent(event) {
  // React component state and props
  if (event.contexts?.react) {
    event.contexts.react = { componentStack: '[redacted]' };
  }

  // Local variables captured in stack frames
  scrubStackFrames(event);

  // Breadcrumb messages, which carry both console output and clicked elements
  scrubBreadcrumbs(event);

  // The request body, which can hold form data, and the query string, which
  // the SDK reads off the page URL. `/register?code=J4KQ-7T2M` carries a
  // facility invite code, so an error on the register page used to send one.
  // The path stays: `/case/<firestoreId>` is an opaque id, not a name.
  if (event.request) {
    delete event.request.data;
    delete event.request.query_string;
    if ('url' in event.request) {
      event.request.url = stripQuery(event.request.url);
    }
  }

  // A formatted log message is the most direct route for PHI into Sentry
  delete event.logentry;

  return event;
}

/**
 * Span attributes whose value is derived from a DOM element, so it carries
 * whatever the element's label says. `browser.web_vital.inp.target` is the
 * element's DOM path, which is the same `htmlTreeAsString` output the
 * breadcrumb allowlist above exists for. `ui.component_name` comes from a
 * component annotation and is a component name today, but it is set from the
 * element too, so it is redacted rather than trusted.
 */
const ELEMENT_DERIVED_SPAN_ATTRIBUTES = ['browser.web_vital.inp.target', 'ui.component_name'];

/**
 * Names the SDK gives a ui span when it has no element name to use. Anything
 * else on a `ui.*` span is derived from the element, so it is redacted.
 */
const SAFE_UI_SPAN_NAMES = new Set(['Click', 'Hover', 'Drag', 'Key press']);

/**
 * before-send-span hook, in the v11 streamed span shape.
 *
 * `beforeSend` never sees a span: spans stream separately, and
 * `beforeSendTransaction` is a no-op in v11. So the element-derived attributes
 * the browser SDK puts on interaction spans need their own pass. Dropping a
 * span here is not possible by design; `ignoreSpans` is the tool for that.
 *
 * @param {object} span - A StreamedSpanJSON: `name`, `attributes`, `status`.
 *
 * @returns {object} The same span, scrubbed in place.
 */
export function scrubSpan(span) {
  const attributes = span?.attributes;
  if (!attributes) return span;

  for (const key of ELEMENT_DERIVED_SPAN_ATTRIBUTES) {
    if (key in attributes) {
      attributes[key] = '[redacted]';
    }
  }

  // dataCollection.urlQueryParams is off, so this should never arrive. Deleted
  // rather than trusted, because it is where a typed search term would land.
  delete attributes['url.query'];

  for (const key of ['url.full', 'url.path']) {
    if (key in attributes) {
      attributes[key] = stripQuery(attributes[key]);
    }
  }

  const op = attributes['sentry.op'];
  if (typeof op === 'string' && op.startsWith('ui.') && !SAFE_UI_SPAN_NAMES.has(span.name)) {
    span.name = '[redacted]';
  }

  return span;
}

export default scrubEvent;
